import { makeAutoObservable } from 'mobx';

export class ThemeStore {
  isDarkMode = false; // Default to light mode for CMS
  isLoading = false;

  constructor() {
    makeAutoObservable(this);
  }

  setDarkMode(value: boolean) {
    this.isDarkMode = value;
    this.saveThemeToStorage(value);
  }

  toggleTheme() {
    this.isDarkMode = !this.isDarkMode;
    this.saveThemeToStorage(this.isDarkMode);
  }

  saveThemeToStorage(isDarkMode: boolean) {
    try {
      localStorage.setItem('theme_mode', isDarkMode ? 'dark' : 'light');
    } catch (error) {
      console.error('Error saving theme to storage:', error);
    }
  }

  loadThemeFromStorage() {
    this.isLoading = true;
    try {
      const savedTheme = localStorage.getItem('theme_mode');
      if (savedTheme !== null) {
        this.isDarkMode = savedTheme === 'dark';
      }
    } catch (error) {
      console.error('Error loading theme from storage:', error);
    } finally {
      this.isLoading = false;
    }
  }

  reset() {
    this.isDarkMode = false;
    this.isLoading = false;
  }

  get themeClasses() {
    return {
      background: this.isDarkMode ? 'bg-zinc-950' : 'bg-gray-50',
      surface: this.isDarkMode ? 'bg-zinc-900' : 'bg-gray-50',
      card: this.isDarkMode ? 'bg-zinc-900/50' : 'bg-white',
      border: this.isDarkMode ? 'border-zinc-800' : 'border-gray-200',
      borderLight: this.isDarkMode ? 'border-zinc-700/50' : 'border-gray-100',
      text: {
        primary: this.isDarkMode ? 'text-white' : 'text-gray-900',
        secondary: this.isDarkMode ? 'text-zinc-300' : 'text-gray-700',
        muted: this.isDarkMode ? 'text-zinc-400' : 'text-gray-500',
        inverse: this.isDarkMode ? 'text-zinc-900' : 'text-white'
      },
      button: {
        primary: 'bg-primary-500 shadow-lg shadow-primary-500/25',
        primaryHover: 'bg-primary-600',
        secondary: this.isDarkMode ? 'bg-zinc-800 border border-zinc-700' : 'bg-gray-100 border border-gray-200',
        disabled: this.isDarkMode ? 'bg-zinc-800 opacity-50' : 'bg-gray-100 opacity-50'
      },
      input: {
        background: this.isDarkMode ? 'bg-zinc-900/50' : 'bg-gray-50',
        border: this.isDarkMode ? 'border-zinc-700' : 'border-gray-200',
        borderFocus: this.isDarkMode ? 'border-primary-500' : 'border-primary-500',
        text: this.isDarkMode ? 'text-white' : 'text-gray-900',
        placeholder: this.isDarkMode ? '#71717A' : '#9CA3AF'
      },
      error: {
        background: this.isDarkMode ? 'bg-red-950/50' : 'bg-red-50',
        border: this.isDarkMode ? 'border-red-800' : 'border-red-200',
        text: this.isDarkMode ? 'text-red-300' : 'text-red-600'
      },
      shadow: this.isDarkMode ? 'shadow-xl shadow-black/25' : 'shadow-lg shadow-gray-200/50'
    };
  }
}